import { useMemo } from 'react';
import { SessionDetailDto } from '@repo/shared/dtos/analytics/SessionDataDto';

export interface SessionExportData {
  sessionId: string;
  userId: string;
  userName: string;
  experienceTitle: string;
  unitId: string;
  date: string;
  time: string;
  durationMinutes: number;
  hasSensorData: boolean;
}

export interface WeeklySessionExportData {
  Week: string;
  Member: string;
  Sessions: number;
  'Total Minutes': number;
}

export const formatUserName = (session: {
  firstName?: string | null;
  lastName?: string | null;
  userId: string;
}): string => {
  if (session.firstName && session.lastName) {
    return `${session.firstName} ${session.lastName}`;
  }
  return `User ${session.userId}`;
};

export const useSessionExportData = (sessions: SessionDetailDto[]) => {
  return useMemo(
    () =>
      sessions.map(session => ({
        sessionId: session.sessionId,
        userId: session.userId,
        userName: formatUserName(session),
        experienceTitle: session.experienceTitle,
        unitId: session.unitId,
        date: new Date(session.createdAt).toLocaleDateString('en-US'),
        time: new Date(session.createdAt).toLocaleTimeString('en-US'),
        durationMinutes: session.durationMinutes,
        hasSensorData: session.hasSensorData,
      })),
    [sessions],
  );
};

const getISOWeekMonday = (date: Date): Date => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayOfWeek = d.getUTCDay();
  const daysToMonday = (dayOfWeek + 6) % 7;
  d.setUTCDate(d.getUTCDate() - daysToMonday);
  return d;
};

const formatWeekRange = (monday: Date, rangeStart?: Date, rangeEnd?: Date): string => {
  const sunday = new Date(Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate() + 6));
  const displayStart = rangeStart && rangeStart > monday ? rangeStart : monday;
  const displayEnd = rangeEnd && rangeEnd < sunday ? rangeEnd : sunday;
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const startStr = displayStart.toLocaleDateString('en-US', opts);
  const endStr = displayEnd.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${startStr} – ${endStr}`;
};

type WeeklyEntry = WeeklySessionExportData & { mondayTs: number };

const groupSessionsByWeek = (
  sessions: SessionDetailDto[],
  startDate?: Date,
  endDate?: Date,
): WeeklySessionExportData[] => {
  const map = new Map<string, WeeklyEntry>();

  for (const session of sessions) {
    const monday = getISOWeekMonday(new Date(session.createdAt));
    const member = formatUserName(session);
    const key = `${session.sessionId}-${monday.getTime()}`;
    const row = map.get(key);

    if (row) {
      row.Sessions += 1;
      row['Total Minutes'] += session.durationMinutes;
    } else {
      map.set(key, {
        mondayTs: monday.getTime(),
        Week: formatWeekRange(monday, startDate, endDate),
        Member: member,
        Sessions: 1,
        'Total Minutes': session.durationMinutes,
      });
    }
  }

  return [...map.values()]
    .sort((a, b) => a.mondayTs - b.mondayTs || a.Member.localeCompare(b.Member))
    .map(({ mondayTs: _, ...row }) => row);
};

export const useWeeklySessionExportData = (
  sessions: SessionDetailDto[],
  startDate?: Date,
  endDate?: Date,
): WeeklySessionExportData[] =>
  useMemo(
    () => groupSessionsByWeek(sessions, startDate, endDate),
    [sessions, startDate, endDate],
  );
